import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Trophy, TrendingUp, RotateCcw, Home, CheckCircle, XCircle } from 'lucide-react';

const PracticeResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { scores, avgScore, config } = location.state || {};

  if (!scores || avgScore === undefined) {
    navigate('/practice');
    return null;
  }

  const answeredCount = scores.filter(s => s.answered).length;
  const notAnsweredCount = scores.filter(s => !s.answered).length;
  const isPassing = avgScore >= 60;

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto">
        {/* Results Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#8B5CF6]/10 rounded-full mb-4">
            {isPassing ? (
              <Trophy className="w-10 h-10 text-[#10B981]" />
            ) : (
              <TrendingUp className="w-10 h-10 text-orange-500" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Practice Session Complete!
          </h1>
          <p className="text-gray-600">
            Here's how you performed
          </p>
        </div>

        {/* Score Card */}
        <Card className="mb-6 border-t-4 border-t-[#8B5CF6]">
          <CardContent className="p-8 text-center">
            <div className="mb-4">
              <div className={`text-6xl font-bold mb-2 ${
                isPassing ? 'text-[#10B981]' : 'text-orange-500'
              }`}>
                {avgScore.toFixed(1)}%
              </div>
              <Badge className={`text-lg px-4 py-1 ${
                isPassing 
                  ? 'bg-[#10B981] hover:bg-[#10B981]/90' 
                  : 'bg-orange-500 hover:bg-orange-600'
              }`}>
                {isPassing ? '✓ Passing' : 'Keep Practicing'}
              </Badge>
            </div>
            <p className="text-gray-600">
              {isPassing 
                ? 'Great job! You\'ve passed this practice session.' 
                : 'You need 60% or above to pass. Keep practicing!'}
            </p>
          </CardContent>
        </Card>

        {/* Statistics */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#8B5CF6]">{scores.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Answered
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{answeredCount}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-600" />
                Not Answered
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{notAnsweredCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Question Breakdown */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Question Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {scores.map((score, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">
                      Question {index + 1}
                    </span>
                    {score.answered ? (
                      <Badge className="bg-green-600">Answered</Badge>
                    ) : (
                      <Badge className="bg-red-600">Not Answered</Badge>
                    )}
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    {score.score}%
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Performance Tips */}
        <Card className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50">
          <CardHeader>
            <CardTitle className="text-purple-900">Performance Tips</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-purple-800">
              {avgScore < 60 && (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">•</span>
                    <span>Review the answers for questions you missed or didn't answer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">•</span>
                    <span>Focus on understanding concepts rather than memorizing answers</span>
                  </li>
                </>
              )}
              {notAnsweredCount > 0 && (
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span>Try to attempt all questions in your next session</span>
                </li>
              )}
              <li className="flex items-start gap-2">
                <span className="text-purple-600 mt-1">•</span>
                <span>Practice regularly to improve your confidence and scores</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-600 mt-1">•</span>
                <span>Use the Question Bank to review specific topics or functions</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="grid md:grid-cols-3 gap-4">
          <Button
            onClick={() => navigate('/practice')}
            className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/90 flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            New Practice Session
          </Button>
          <Button
            onClick={() => navigate('/question-bank')}
            variant="outline"
            className="flex items-center justify-center gap-2"
          >
            Review Questions
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default PracticeResultsPage;
